<template><section><!-- ---
title: 计算机网络系列之DNS以及网络域名解析
date: 2022-10-16
tags: 计算机网络
set: Network
--- -->
<p>因特网采用层次树状结构命名，任何一个连接在因特网上的主机或者路由器，都有一个唯一的层次结构的名字，即域名。从语法上讲，每个域名都是由标号序列组成，而各标号之间用&quot;.&quot;隔开。例如下面的域名由三个标号组成，其中标号com是顶级域名，标号cctv是二级域名，标号mail是三级域名。标号不得超过63个字符，也不区分大小写，总域名不超过255个字符。各级标号域名由上一级的域名管理机构管理，顶级域名由ICANN管理</p>
<div style="display:flex;justify-content:center;"><img src="https://zhangmingemma.github.io/dist/images/2022-10-16/1.jpeg" style="display:inline-block; margin-bottom:16px; width:300px;"></div>
<p>因特网上的DNS域名服务器也是按照层次结构安排的，一个域名服务器都只对域名体系中的一部分进行管辖。每个域名服务器一般都会做数据备份，有一个主域名服务器，若干辅助域名服务器。根据域名服务器所起的作用，可以把域名服务器划分为：</p>
<ol>
<li>根域名服务器：层次最高的域名服务器，也是最重要的域名服务器，所有根域名服务器都知道顶级域名服务器的域名和IP地址。如果因特网上任何一个域名无法解析时，就会求助于根域名服务器。目前全球有a.rootservers,net ~ m.rootservers.net 13个跟域名服务器，对应无数台机器，分布在全球很多地方。很多根域名服务器在查询过程中，并不会将IP地址直接返回，而是告诉本地域名服务器下一步的顶级域名服务器是哪一个。</li>
<li>顶级域名服务器：负责管理在该项域名下所有的二级域名，当收到DNS查询时，给出响应的回答，可能是IP地址，也可能是下一步应该找的域名服务器的IP地址</li>
<li>权限域名服务器：当收到DNS查询时，给出响应的回答，可能是IP地址，也可能是下一步应该找的域名服务器的IP地址</li>
<li>本地域名服务器：并不属于上面图片中的域名服务器层次结构，但当一个主机发出DNS请求时，这个查询请求报文就发送给本地域名服务器，当所查询的主机属于同一个本地ISP时，就能直接给出IP地址，不需逐层向上查询。</li>
</ol>
<div style="display:flex;justify-content:center;"><img src="https://zhangmingemma.github.io/dist/images/2022-10-16/2.jpeg" style="display:inline-block; margin-bottom:16px; width:500px;"></div>
<h2>DNS域名解析过程</h2>
<p>整个DNS域名的解析过程如下：</p>
<ol>
<li>主机向本地域名服务器发起查询，采用的是<strong>递归查询</strong>，所谓递归查询就是如果主机所询问的本地域名服务器不知道准确的IP地址，那么本地域名服务器会作为请求方，向上级域名服务器发起请求</li>
<li>本地域名服务器向根域名服务器发起查询，采用的是<strong>迭代查询</strong>，所谓迭代查询就是根域名服务器收到本地域名服务器发出的报文时，要么给出IP地址，要么就是告诉本地域名服务器下一步要查询的域名服务器</li>
</ol>
<div style="display:flex;justify-content:center;"><img src="https://zhangmingemma.github.io/dist/images/2022-10-16/4.jpeg" style="display:inline-block; margin-bottom:16px; width:500px;"></div>
<p>以上面的图片为例，如果<code v-pre>m.xyz.com</code>要向<code v-pre>y.abc.com</code>发送消息，就首先会向本地域名服务器<code v-pre>dns.xyz.com</code>发起DNS查询，本地域名服务器采用迭代查询的方式，向一个根域名服务器发起查询，根域名服务器告诉本地域名服务器顶级域名服务器<code v-pre>dns.com</code>的IP地址，本地域名服务器向<code v-pre>dns.com</code>发起查询，返回<code v-pre>dns.abc.com</code>的IP地址，本地域名服务器再向<code v-pre>dns.abc.com</code>发起查询，返回<code v-pre>y.abc.com</code>的IP地址。</p>
<h2>DNS域名解析提速</h2>
<p>如果每次都进行DNS域名的查询，会耗费大量的成本和耗时，也会加重根域名服务器的负荷，因此：</p>
<ol>
<li>为了提高DNS的查询效率，减轻根域名服务器的负荷，都会进行缓存，告诉缓存用来存放最近查询过的域名以及何处获得域名的映射表。</li>
<li>实现全局负载均衡，实现不同地域服务器的流量调配能够保证用户的请求被离用户最近或者服务质量最好的服务器来处理，确保服务质量。</li>
</ol>
<h2>DNS域名解析问题</h2>
<p>因为DNS域名解析导致的常见问题有：</p>
<ol>
<li>运营商劫持（DNS劫持）：通过劫持DNS域名服务器，获取域名解析记录的控制权，修改域名的解析结果，导致用户访问到假网址，从而窃取资料或者破坏原有服务的正常服务</li>
</ol>
<div style="display:flex;justify-content:center;"><img src="https://zhangmingemma.github.io/dist/images/2022-10-16/4.jpeg" style="display:inline-block; margin-bottom:16px; width:500px;"></div>
<ol start="2">
<li>DNS解析域名时缓存解析结果：我们在开发的过程中经常会出现，配置了代理没有生效，或者举例联通用户在手机浏览器中输入baidu.com，本来应该是由一个本地域名服务器向百度权限域名服务器查询应该访问哪台服务器，本地域名服务器根据返回结果进行查询，并返回给用户。但当本地域名服务器有缓存的时候，就不会发起查询，而是直接返回缓存中的结果，如果IP地址已经发生了改变，那就会导致用户访问不到</li>
</ol>
<h2>额外知识点</h2>
<ul>
<li>DNS为什么使用UDP？主要原因是为了避免使用TCP造成的连接时延，为了得到IP地址，往往会向多个域名服务器发起查询请求，如果使用TCP，会让DNS服务变得很慢，会造成页面的过长等待</li>
<li>DNS如何实现负载平衡？一个域名可能对应多个服务器地址，当用户发起DNS请求时，DNS服务器会返回一个IP集合，用户一般选择排在前面的地址发送请求，将用户的请求分配到不同的服务器上，实现负载均衡</li>
</ul>
<p>参考文献：
[1] 计算机网络 谢希仁著
[2] <a href="https://juejin.cn/post/6844903987796246542">全面理解DNS及HTTPDNS</a></p>
</section></template>

